import React, { useEffect } from 'react'
import { StepWizardChildProps } from 'react-step-wizard'

import { FormProvider, useForm } from 'react-hook-form'
import FButton from '../../../../components/BaseComponents/FButton'
import FInput from '../../../../components/BaseComponents/FInput'
import { errors } from '../../../../components/BaseComponents/constants'
import SBox from '../../../../components/opinionless/SBox'
import { useTranslation } from '../../../../i18n'

import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useSTheme } from 'styles/theme'
import { ga4_useTitle } from 'utils/ga4'
import { useBenefitsAnswerQueryQuery, useBenefitsAnswersMutation } from '../../../../types/graphql'

interface Props {
    personId?: any
    updatePercentage: any
}

const CalFreshWizardContainer: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const form = useForm()
    const { handleSubmit } = form

    const [saveBenefitAnswer, { loading }] = useBenefitsAnswersMutation()
    const { data, loading: queryLoading } = useBenefitsAnswerQueryQuery({
        variables: { personId: props.personId },
        fetchPolicy: 'network-only',
    })

    ga4_useTitle('CalFresh')

    const onFormSubmit = async (values) => {
        await saveBenefitAnswer({
            variables: {
                answers: [
                    {
                        personId: props.personId,
                        category: 'CAL_FRESH_AMOUNT',
                        answer: values.calfresh_amount,
                    },
                ],
            },
        })
        props.nextStep()
    }

    useEffect(() => {
        if (data) {
            const savedValue = data.benefitsAnswers.find((answer: any) => answer.category === 'CAL_FRESH_AMOUNT')
                ?.answer
            form.setValue('calfresh_amount', savedValue || null)
        }
    }, [data])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.benefits,
        WIZARD_STEPS_MAPPING.benefits.calFresh,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <>
            <FButtonPrevious onClick={props.previousStep} />
            <LBTextHeader
                sx={{
                    textAlign: 'left',
                    fontSize: '32px',
                    color: theme.colors.secondaryColor,
                    lineHeight: '120%',
                    fontFamily: theme.fonts.longBeach.body,
                    fontWeight: '900',
                }}
            >
                {t('calfresh_heading')}
            </LBTextHeader>
            <LBTextSubHeader
                color={theme.colors.blackLabelfont}
                dangerouslySetInnerHTML={{
                    __html: t('calfresh_p1'),
                }}
            />
            <FormProvider {...form}>
                <SBox as="form" onSubmit={handleSubmit(onFormSubmit)}>
                    <FInput
                        color={theme.colors.blackLabelfont}
                        labelColor={theme.colors.blackLabelFontAlpha}
                        id="calfresh_amount"
                        name="calfresh_amount"
                        label={t('calfresh_amount_label')}
                        type="number"
                        placeholder={t('amount_in_dollars_placeholder')}
                        ref={form.register({
                            required: errors.default_required_key as string,
                            validate: {
                                positiveNumber: (value) => parseInt(value) >= 0 || 'positive_integer_validation',
                            },
                        })}
                    />
                    <FButton type="submit" loading={loading} ga4_key={t('continue', { lang: 'en' })}>
                        {t('save_and_continue')}
                    </FButton>
                </SBox>
            </FormProvider>
        </>
    )
}

export default CalFreshWizardContainer
